import { Auth0Service } from './auth/auth0.service'
import { MockAuthService } from './auth/mock-auth.service'
import type { IAuthService } from './auth/auth.service.interface'

export async function createAuthService(): Promise<IAuthService> {
  const useMockServices = import.meta.env.VITE_ENABLE_MOCK_SERVICES === 'true'

  if (useMockServices) {
    return new MockAuthService()
  }

  // Validate required environment variables
  const domain = import.meta.env.VITE_AUTH0_DOMAIN
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE

  if (!domain || !clientId || !audience) {
    console.error('[Auth] Missing required environment variables:', {
      domain: domain ? '✓' : '✗ MISSING',
      clientId: clientId ? '✓' : '✗ MISSING',
      audience: audience ? '✓' : '✗ MISSING',
    })
    throw new Error(
      'Auth0 configuration is incomplete. Please check your environment variables (VITE_AUTH0_DOMAIN, VITE_AUTH0_CLIENT_ID, VITE_AUTH0_AUDIENCE).'
    )
  }

  return new Auth0Service({
    domain,
    clientId,
    authorizationParams: {
      audience,
      redirect_uri: import.meta.env.VITE_AUTH0_CALLBACK_URL || window.location.origin + '/callback',
      // Note: organization parameter is NOT set here intentionally
      // Auth0 Organizations are configured on the Auth0 dashboard and
      // the organization ID is automatically included in the session after login
      // The SDK will retrieve and use the organization from the cached session

      // Session duration configuration:
      // max_age controls how long the authentication is considered valid
      // To get refresh tokens (and avoid the 15-min sign-out cycle), the Auth0 API
      // configured in VITE_AUTH0_AUDIENCE must have "Allow Offline Access" enabled.
      // The Site Compass API (port 5000) must also be configured to validate tokens
      // against the same audience identifier.
      max_age: 604800, // 7 days in seconds (7 * 24 * 60 * 60)
    },
    // Use localStorage to persist auth state across page refreshes
    // This is the Auth0 SDK default and recommended approach
    cacheLocation: 'localstorage',
    // Enable refresh tokens for automatic token renewal
    // When remember me is enabled, refresh tokens keep the session alive up to max_age
    useRefreshTokens: true,
    // Use refresh token rotation for security
    useRefreshTokensFallback: true,
  })
}
